import "./style.scss";

import { Modal } from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import CloudUpload from "assets/img/svg/cloudUpload";
import UploadSuccess from "assets/img/svg/uploadSuccess";
import PickerCompany from "components/BPPickerCompany";
import type { IDocumentTypeFieldsValue } from "components/shared/DocumentTypeFields";
import DocumentTypeFields, {
    appendDocumentTypeFields,
    emptyDocumentTypeFields,
    isDocumentTypeFieldsValid,
} from "components/shared/DocumentTypeFields";
import type { ITaxProfile } from "helpers/beneficiaryDocuments";
import { expectedTypes, getCurrentDocumentId, getDocumentStatus, todayDateOnly } from "helpers/beneficiaryDocuments";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString } from "helpers/utils";
import { useIsMobile } from "hooks/useIsMobile";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IBeneficiaryDocumentType } from "store/beneficiaryDocumentTypesStore";
import type { IDocument } from "store/documentStore";
import type { IBeneficiariesCompanies } from "store/types";

import { displayMobileNavBtn } from "../BPMobileNavBtn";
import StyledButton from "../StyledButton";
import StyledTextField from "../StyledTextField/StyledTextField";
import { getFileSize, normaliseFilename } from "./../../../helpers/utils";

type TProps = {
    isModalOpen: boolean;
    onClose: () => void;
    onFileAdd: (file: { formData: FormData }) => void;
    pickerCompany?: boolean;
    multipleCompany?: boolean;
    companies?: IBeneficiariesCompanies[];
    chosenCompanies?: IBeneficiariesCompanies[];
    setChosenCompanies?: Dispatch<SetStateAction<IBeneficiariesCompanies[]>>;
    disabledPickerCompanies?: boolean;
    /**
     * Add subtitle to the add document modal
     */
    modalSubtitle?: string;
    /**
     * Company document types (tracking enabled): lets the user pick a type and a validity period,
     * sent as `documentTypeId` / `validFrom` / `validUntil` multipart fields.
     */
    documentTypes?: IBeneficiaryDocumentType[];
    /** Owner of the documents: preselects a type expected from them and words the hint (tracking enabled) */
    beneficiary?: ITaxProfile;
    /** Existing documents of the beneficiary, to tell when the upload supersedes a current one */
    documents?: IDocument[];
    /** Type to preselect when the modal opens (e.g. the expected document the author clicked) */
    defaultDocumentTypeId?: number | null;
};

/**
 * Planche 3: the first type expected from the beneficiary is preselected, "other document" when none is.
 * Without a beneficiary (author portal: the types given are already the expected ones) the first type is.
 */
const defaultTypeFields = (
    documentTypes?: IBeneficiaryDocumentType[],
    beneficiary?: ITaxProfile,
): IDocumentTypeFieldsValue => ({
    ...emptyDocumentTypeFields,
    documentTypeId: beneficiary
        ? expectedTypes(documentTypes ?? [], beneficiary)[0]?.id ?? null
        : documentTypes?.[0]?.id ?? null,
});

/** What the tracking will show once the document is saved (the green / pink banner) */
const previewOf = (
    t: (key: string, options?: Record<string, unknown>) => string,
    type: IBeneficiaryDocumentType,
    value: IDocumentTypeFieldsValue,
    documents: IDocument[] = [],
): { text: string; warning: boolean } | undefined => {
    if (type.requiresValidity && !value.validUntil) return undefined;
    const today = todayDateOnly();
    const date = (dateOnly: string) =>
        formatLocaleDateString({ date: dateOnly, options: { numeric: true, timeZone: "UTC" } });

    if (value.validFrom && value.validFrom > today) {
        return {
            text: t("BeneficiaryDocuments.Documents.PreviewUpcoming", { type: type.name, date: date(value.validFrom) }),
            warning: false,
        };
    }
    if (getDocumentStatus({ status: "valid", validUntil: value.validUntil }, today) === "expired") {
        return {
            text: t("BeneficiaryDocuments.Documents.PreviewExpired", {
                type: type.name,
                date: date(value.validUntil as string),
            }),
            warning: true,
        };
    }

    // Does the upload become the current document of its type? Same rule as the table (latest end date wins)
    const upload = { id: Number.MAX_SAFE_INTEGER, status: "valid" as const, ...value, documentTypeId: type.id };
    const supersedes =
        getCurrentDocumentId(type.id, documents, today) !== null &&
        getCurrentDocumentId(type.id, [...documents, upload], today) === upload.id;

    return {
        text: [
            value.validUntil
                ? t("BeneficiaryDocuments.Documents.PreviewUntil", { type: type.name, date: date(value.validUntil) })
                : t("BeneficiaryDocuments.Documents.PreviewNoEnd", { type: type.name }),
            supersedes ? t("BeneficiaryDocuments.Documents.PreviewHistory") : "",
        ]
            .filter(Boolean)
            .join(" "),
        warning: false,
    };
};

const AddDocument = ({
    isModalOpen,
    onClose,
    onFileAdd,
    pickerCompany = false,
    multipleCompany = false,
    companies,
    chosenCompanies,
    setChosenCompanies,
    disabledPickerCompanies = false,
    modalSubtitle,
    documentTypes,
    beneficiary,
    documents,
    defaultDocumentTypeId,
}: TProps) => {
    const [t] = useTranslation();
    const [fileName, setFileName] = useState<string>();
    const [originalFileName, setOriginalFilename] = useState<string>("");
    const [file, setFile] = useState<File>();
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [typeFields, setTypeFields] = useState<IDocumentTypeFieldsValue>(() =>
        defaultTypeFields(documentTypes, beneficiary),
    );
    const hasTypes = !!documentTypes?.length;
    const areTypeFieldsValid = !hasTypes || isDocumentTypeFieldsValid(typeFields, documentTypes ?? []);
    const selectedType = documentTypes?.find((type) => type.id === typeFields.documentTypeId);
    // Planche 3: what the tracking will show once the document is saved
    const preview = selectedType ? previewOf(t, selectedType, typeFields, documents) : undefined;
    const { isMobile } = useIsMobile();

    useEffect(() => {
        if (!isModalOpen) return;
        const fields = defaultTypeFields(documentTypes, beneficiary);
        const preselected = documentTypes?.some((type) => type.id === defaultDocumentTypeId);
        setTypeFields(preselected ? { ...fields, documentTypeId: defaultDocumentTypeId as number } : fields);
    }, [isModalOpen, documentTypes, beneficiary, defaultDocumentTypeId]);

    const handleFileNameChange = (e: any) => {
        const fileName = normaliseFilename(e.target.value);
        setFileName(fileName);
    };

    const handleFileInput = (e: any) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
            !fileName && setFileName(e.target.files[0].name ? e.target.files[0].name.split(".")[0] : "");
            setOriginalFilename(e.target.files[0].name ? e.target.files[0].name.split(".")[0] : "");
        }
    };

    // TODO: block file type that aren't CSV or XLS
    const handleDropOver = (e: any) => {
        e.preventDefault();
        setIsDraggingOver(false);
        if (e.dataTransfer.items) {
            // Use DataTransferItemList interface to access the file(s)
            [...e.dataTransfer.items].forEach((item, i) => {
                // If dropped items aren't files, reject them
                if (item.kind === "file") {
                    const file = item.getAsFile();
                    setFile(file);
                    setFileName(file.name ? file.name.split(".")[0] : "");
                    setOriginalFilename(file.name ? file.name.split(".")[0] : "hello");
                    return;
                }
            });
        }
    };

    const handleDragOver = (e: any) => {
        e.preventDefault();
        if (isDraggingOver) return;
        setIsDraggingOver(true);
    };

    const handleValidate = () => {
        if (!file) return;
        const formData = new FormData();
        const _fileName = normaliseFilename(fileName || file.name);
        formData.append("file", file, _fileName);
        if (hasTypes) appendDocumentTypeFields(formData, typeFields);
        const param = { formData };
        onFileAdd(param);
        onClose();
        if (isMobile) displayMobileNavBtn(true);
        setFile(undefined);
        setFileName("");
        setOriginalFilename("");
    };

    const closeDropDocModal = () => {
        onClose();
        if (isMobile) displayMobileNavBtn(true);
        setFile(undefined);
        setFileName(undefined);
    };

    return (
        <>
            <Modal open={isModalOpen} onClose={closeDropDocModal}>
                <div className="add-document">
                    <div className="add-document-modal">
                        <div className="add-document-header">
                            <div>
                                <div className="add-document-header-title">{t("AddDocument.AddDocument")}</div>
                                {modalSubtitle && <SmallBody color="grey">{modalSubtitle}</SmallBody>}
                            </div>
                            <div className="add-document-header-cross" onClick={closeDropDocModal}>
                                <GridCloseIcon />
                            </div>
                        </div>
                        <div className="add-document-body">
                            {!!pickerCompany && (
                                <div className="add-document-company-picker">
                                    <span className="add-document-label">
                                        {t("AddDocument.Recipient")}
                                        {chosenCompanies!.length > 1 && "s"}
                                    </span>
                                    <PickerCompany
                                        data={companies!}
                                        selectedData={chosenCompanies!}
                                        // TODO : Put multipleCompany instead of false (wait backend update)
                                        setSelectedData={setChosenCompanies!}
                                        multipleSeleted={false}
                                        disabled={disabledPickerCompanies}
                                    />
                                </div>
                            )}
                            <div className="add-document-dropzone-group">
                                <span className="add-document-label">Document</span>
                                <div
                                    className={`add-document-dropzone ${
                                        isDraggingOver ? "add-document-dropzone-dragging" : ""
                                    } ${file && "accept"}`}
                                    onDrop={handleDropOver}
                                    onDragOver={handleDragOver}
                                    onDragLeave={() => setIsDraggingOver(false)}
                                >
                                    <input
                                        className="add-document-input-file"
                                        type="file"
                                        name="file"
                                        id="file"
                                        accept="*"
                                        onChange={handleFileInput}
                                    />
                                    <DropZoneContent file={file} originalFileName={originalFileName} />
                                </div>
                            </div>
                            <div className="add-document-input-group">
                                <span className="add-document-label">{t("AddDocument.Name")}</span>
                                <StyledTextField
                                    name="search"
                                    id="search"
                                    value={fileName ?? ""}
                                    placeholder={t("AddDocument.Name")}
                                    onChange={handleFileNameChange}
                                />
                            </div>
                            {hasTypes && (
                                <DocumentTypeFields
                                    documentTypes={documentTypes ?? []}
                                    value={typeFields}
                                    onChange={setTypeFields}
                                    beneficiary={beneficiary}
                                />
                            )}
                            {preview && (
                                <div className={`add-document-preview${preview.warning ? " warning" : ""}`}>
                                    <span className="add-document-preview-dot" />
                                    <span>{preview.text}</span>
                                </div>
                            )}
                            <StyledButton
                                buttonType="primary"
                                disabled={!file || !areTypeFieldsValid}
                                onClick={handleValidate}
                            >
                                {t(hasTypes ? "BeneficiaryDocuments.Documents.Validate" : "AddDocument.Validate")}
                            </StyledButton>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

interface IDropZoneContent {
    file: File | undefined;
    originalFileName: string;
}

const DropZoneContent = ({ file, originalFileName }: IDropZoneContent) => {
    const { isMobile } = useIsMobile();
    const [t] = useTranslation();
    return (
        <>
            <div className="add-document-dropzone-headline">
                <div className="add-document-icon">
                    {file ? <UploadSuccess style={{ height: "32px", width: "32px" }} /> : <CloudUpload />}
                </div>
                {!file && (
                    <div className="add-document-text">
                        {isMobile ? t("AddDocument.MobileDropFile") : t("AddDocument.DragHere")}
                    </div>
                )}
                {!file && !isMobile && <div className="add-document-subtext">{t("AddDocument.Or")}</div>}
                <div className="add-document-subtext">
                    <label htmlFor="file" className={`add-document-link ${file && "success"}`}>
                        {file ? originalFileName || file.name : t("AddDocument.ChooseFile")}
                    </label>
                    {file && (
                        <span className={`add-document-subtext ${file && "success"}`}> {getFileSize(file.size)}</span>
                    )}
                </div>
                {file && <span className="add-document-success-text">{t("ImportData.UploadSuccess")}</span>}
            </div>
            {!file && (
                <div className="add-document-subtext add-document-info">
                    {t("AddDocument.FormatMustBe")} <br />
                    {t("AddDocument.FileSizeAtMost")} <span className="add-document-bold">5mb</span>
                </div>
            )}
        </>
    );
};

export default AddDocument;
